import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

import { scrollToBlock } from '../../../helpers/scrollToBlock';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import CoverageStrip from '../GscTools/components/CoverageStrip';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { downloadCsv } from '../GscTools/csv';
import {
    buildSeries,
    coverageFor,
    hasDuplicateKeys,
    metricsFor,
    normalizeTotals,
    reportsPosition,
    sumRows,
} from '../GscTools/metrics';
import {
    DETAIL_MAX_DAYS,
    defaultPeriod,
    detailPeriod,
    formatPeriod,
    periodEndingOn,
} from '../GscTools/period';
import { readParams, writeParams } from '../GscTools/url';

import ExtraControls from './components/ExtraControls';
import TopicComparison from './components/TopicComparison';
import TopicContributors from './components/TopicContributors';
import TopicEditor from './components/TopicEditor';
import TopicRules from './components/TopicRules';
import TopicTrend from './components/TopicTrend';
import { buildComparisonCsv, buildContributorsCsv } from './exportRows';
import { fetchTopicContributors, fetchTopicDaily, fetchTopicTotals } from './query';
import { contributorRows, orderedComparisonRows } from './rows';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';

const IDLE_TOTALS = { status: 'idle', error: null, current: null, baseline: null };
const IDLE_TREND = {
    status: 'idle',
    error: null,
    baselineError: null,
    current: null,
    baseline: null,
};
const IDLE_CONTRIBUTORS = { status: 'idle', error: null, chunk: null, loadingMore: false };

const DEFAULT_METRIC = 'clicks';

/**
 * Topic Clusters — one Search Console property's search traffic, read through manual query topics
 * the user writes for this session, compared topic by topic and opened one topic at a time.
 *
 * A topic is a query rule and nothing more. Nothing is discovered, clustered or classified — not
 * by Google, which has no notion of a topic, and not by this app — so every figure on the page is
 * the answer to the filters a topic compiles to: the topics are stated first, each with its policy,
 * its case rule and the filters it was sent as, and the export carries them with the numbers.
 *
 * Topics are never a partition of anything. Each is requested on its own, a query can meet two
 * topics and is then counted in both, and the queries Search Console withholds below its own
 * thresholds are in no topic at all — so no figure is summed across topics, no total row is drawn,
 * and nothing is ever called the property's traffic or "unclassified" traffic.
 *
 * Requests per applied scope: one totals call per topic, holding both windows, so a topic that
 * fails leaves the others standing; then, for the active topic only, its daily series for each
 * window and the first chunk of its contributors for the detail window. Picking another topic
 * re-asks for those two blocks; switching block 04 between queries and pages re-asks for that block
 * alone. A session with no topics is never sent — the report refuses it rather than quietly reading
 * every query on the property.
 *
 * Three kinds of staleness are guarded separately: the scope generation (every apply and refresh),
 * the trend generation (every pick) and the contributors generation (every pick and every switch),
 * so a slow answer can never land under a heading it does not belong to.
 *
 * Nothing fetched is persisted. The topics are session-only: they live in this page's address once
 * applied, so a reload or a shared link reproduces them along with the figures.
 */
class TopicClusters extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleTopicsChange = this.handleTopicsChange.bind(this);
        this.handleRestoreTopics = this.handleRestoreTopics.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleMode = this.handleMode.bind(this);
        this.handleSelectTopic = this.handleSelectTopic.bind(this);
        this.handleContributors = this.handleContributors.bind(this);
        this.handleTrendMetric = this.handleTrendMetric.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleExportComparison = this.handleExportComparison.bind(this);
        this.handleExportContributors = this.handleExportContributors.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();
        // The two blocks a picked topic fills, so a pick can bring them into view.
        this.detailsRef = createRef();

        this.generation = 0;
        this.trendGeneration = 0;
        this.contributorsGeneration = 0;
        this.isMounted_ = false;

        const period = defaultPeriod();

        // The address is read once, here, so the controls show the report it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            latestDate: '',

            draft: { ...initialDraft(period), ...this.urlDraft },

            applied: null,
            loading: false,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            totals: {},
            trend: { ...IDLE_TREND },
            contributors: { ...IDLE_CONTRIBUTORS },
            trendMetric: DEFAULT_METRIC,
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                // Dates that came from the address are the user's own: the probe still runs, for
                // the preset anchor, but it does not move them.
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity, so the window the user is
     * offered has something in it rather than ending in days Search Console has not filled yet.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                if (state.draft.siteUrl !== siteUrl) return null;
                // Kept as well as applied: the date presets count back from this day, so one
                // click lands on days the property has data for.
                if (!seedDates) return { latestDate: latest };

                return { latestDate: latest, draft: { ...state.draft, ...periodEndingOn(latest) } };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    /**
     * The window block 04 covers. It is the only request carrying a query or page dimension, which
     * multiplies the rows retrieved per day, so it is held to the shared detail bound rather than
     * holding the whole report to it — and says so when that shortens it.
     */
    contributorsWindow(scope) {
        return detailPeriod(scope.current, DETAIL_MAX_DAYS);
    }

    activeTopic(scope) {
        return scope?.topics.find((item) => item.name === scope.topic) ?? null;
    }

    /** The metric the trend draws, held to the ones this surface actually reports. */
    trendMetric() {
        const searchType = this.state.applied?.searchType ?? this.state.draft.searchType;
        return metricsFor(searchType).includes(this.state.trendMetric)
            ? this.state.trendMetric
            : DEFAULT_METRIC;
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                }
            },
        );
    }

    handleTopicsChange(topics) {
        this.setState((state) => ({ draft: { ...state.draft, topics } }));
    }

    /** Throws away an unapplied edit: the topics go back to the ones the report was read under. */
    handleRestoreTopics() {
        this.setState((state) =>
            state.applied ? { draft: { ...state.draft, topics: [...state.applied.topics] } } : null,
        );
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    /**
     * The trend opens on the applied metric. Only an apply moves it there — a refresh repeats the
     * same scope, and the card the user picked since is still the one they are reading.
     */
    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.setState({ trendMetric: scope.metric });
        this.load(scope);
    }

    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied);
    }

    /**
     * A change to the applied scope that needs no new totals — the order, the pick, the
     * contributor view — kept in the draft and the address too, so what the page shows, what the
     * controls say and what a shared link opens never disagree.
     */
    patchApplied(patch, draftPatch, callback) {
        const applied = { ...this.state.applied, ...patch };
        writeParams(reportParamsFromScope(applied), URL_KEYS);
        this.setState((state) => ({ applied, draft: { ...state.draft, ...draftPatch } }), callback);
        return applied;
    }

    /** Re-orders the topics already retrieved. Applied in memory, so it never costs a request. */
    handleMode(mode) {
        if (this.state.applied) this.patchApplied({ mode }, { mode });
    }

    /**
     * Runs the applied scope: every topic's totals at once, each landing on its own, and the
     * active topic's detail beside them.
     */
    load(scope) {
        this.generation += 1;
        const generation = this.generation;

        // Written here rather than in handleApply so the address also follows a refresh, and so
        // it only ever describes a scope that was actually run.
        writeParams(reportParamsFromScope(scope), URL_KEYS);

        this.setState({
            applied: scope,
            controlsOpen: false,
            loading: true,
            fetchedAt: new Date().toISOString(),
            totals: Object.fromEntries(
                scope.topics.map((topic) => [topic.name, { ...IDLE_TOTALS, status: 'loading' }]),
            ),
        });

        for (const topic of scope.topics) this.loadTotals(scope, topic, generation);

        this.loadDetail(scope);
    }

    /** Records one topic's outcome without disturbing the others'. */
    setTotals(name, value) {
        this.setState((state) => ({ totals: { ...state.totals, [name]: value } }));
    }

    /**
     * Block 02 — one topic's dimensionless totals for each window. `loading` is cleared as soon as
     * the first topic lands; the other blocks carry their own states.
     */
    async loadTotals(scope, topic, generation) {
        const hasPosition = reportsPosition(scope.searchType);

        try {
            const { current, baseline } = await fetchTopicTotals({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                topic,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({ loading: false });
            this.setTotals(topic.name, {
                status: 'done',
                error: null,
                current: normalizeTotals(current, hasPosition),
                baseline: scope.baseline ? normalizeTotals(baseline, hasPosition) : null,
            });
        } catch (err) {
            console.log('totals', topic.name, err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({ loading: false });
            this.setTotals(topic.name, {
                ...IDLE_TOTALS,
                status: 'error',
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    /**
     * Picking a topic re-asks for blocks 03 and 04 under it; picking the one already open, or
     * clearing the chip, drops the pick — and with it whatever those blocks were still waiting for.
     */
    handleSelectTopic(name) {
        const scope = this.state.applied;
        if (!scope) return;

        const topic = name && name !== scope.topic ? name : null;
        const applied = this.patchApplied(
            { topic },
            { topic: topic ?? '' },
            // Every pick brings the blocks into view: they sit below the fold, and a pick that
            // left the page where it was would look like nothing happened. scrollToBlock moves
            // the page only when the block is not already readable.
            () => topic && scrollToBlock(this.detailsRef.current),
        );

        this.loadDetail(applied);
    }

    /**
     * Switches block 04 between queries and pages. Only that block is re-asked: the comparison and
     * the trend are query-scoped totals and do not change with what the contributors are keyed by.
     */
    handleContributors(view) {
        const scope = this.state.applied;
        if (!scope || scope.contributors === view) return;

        const applied = this.patchApplied({ contributors: view }, { contributors: view });
        const topic = this.activeTopic(applied);
        if (topic) this.loadContributors(applied, topic, { more: false });
    }

    /** Blocks 03 and 04 for the active topic, or both back to waiting for a pick. */
    loadDetail(scope) {
        const topic = this.activeTopic(scope);

        if (!topic) {
            // Bumped so answers still in flight for the previous pick are dropped on arrival.
            this.trendGeneration += 1;
            this.contributorsGeneration += 1;
            return this.setState({
                trend: { ...IDLE_TREND },
                contributors: { ...IDLE_CONTRIBUTORS },
            });
        }

        this.loadTrend(scope, topic);
        this.loadContributors(scope, topic, { more: false });
    }

    /**
     * Block 03 — the topic's daily rows for each window, as separate calls. A current window that
     * fails is the block's error; a baseline that fails only withholds the comparison, and says so.
     */
    async loadTrend(scope, topic) {
        this.trendGeneration += 1;
        const trendGeneration = this.trendGeneration;
        const hasPosition = reportsPosition(scope.searchType);

        this.setState({ trend: { ...IDLE_TREND, status: 'loading' } });

        const request = async (period) => {
            if (!period) return null;

            const rows = await fetchTopicDaily({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                topic,
                period,
            });

            return {
                series: buildSeries(rows, period, hasPosition),
                coverage: coverageFor(rows, period),
                totals: sumRows(rows, hasPosition),
            };
        };

        const [current, baseline] = await Promise.allSettled([
            request(scope.current),
            request(scope.baseline),
        ]);

        if (!this.isMounted_ || trendGeneration !== this.trendGeneration) return;

        const reason = (result) =>
            result.reason?.message ?? this.props.t('global.something_went_wrong');

        if (current.status === 'rejected') {
            console.log('trend', current.reason);
            return this.setState({
                trend: { ...IDLE_TREND, status: 'error', error: reason(current) },
            });
        }

        this.setState({
            trend: {
                status: 'done',
                error: null,
                current: current.value,
                baseline: baseline.status === 'fulfilled' ? baseline.value : null,
                baselineError: baseline.status === 'rejected' ? reason(baseline) : null,
            },
        });
    }

    /**
     * Block 04 — one chunk of the topic's queries or pages, appended to the ones already held when
     * loading more. A key returned twice stops retrieval rather than being counted twice.
     */
    async loadContributors(scope, topic, { more }) {
        const previous = more ? this.state.contributors.chunk : null;
        if (more && (previous?.nextOffset ?? null) === null) return;

        // Loading more continues the current retrieval; anything else starts a new one, and an
        // answer to the previous one is dropped when it lands.
        if (!more) this.contributorsGeneration += 1;
        const contributorsGeneration = this.contributorsGeneration;

        this.setState((state) => ({
            contributors: more
                ? { ...state.contributors, loadingMore: true, error: null }
                : { ...IDLE_CONTRIBUTORS, status: 'loading' },
        }));

        try {
            const result = await fetchTopicContributors({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                topic,
                period: this.contributorsWindow(scope).period,
                view: scope.contributors,
                offset: more ? previous.nextOffset : 0,
            });

            const rows = [...(previous?.rows ?? []), ...result.rows];
            if (hasDuplicateKeys(rows)) throw new Error(this.props.t('tools.gsc.duplicate_rows'));

            if (!this.isMounted_ || contributorsGeneration !== this.contributorsGeneration) return;

            this.setState({
                contributors: {
                    status: 'done',
                    error: null,
                    loadingMore: false,
                    chunk: { ...result, rows },
                },
            });
        } catch (err) {
            console.log('contributors', err);
            if (!this.isMounted_ || contributorsGeneration !== this.contributorsGeneration) return;

            this.setState((state) => ({
                contributors: {
                    ...state.contributors,
                    // A failure while loading more keeps the rows already retrieved; a failure on
                    // the first chunk has nothing to keep.
                    status: state.contributors.chunk ? 'done' : 'error',
                    loadingMore: false,
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            }));
        }
    }

    handleLoadMore() {
        const scope = this.state.applied;
        const topic = this.activeTopic(scope);
        if (topic) this.loadContributors(scope, topic, { more: true });
    }

    handleTrendMetric(metric) {
        this.setState({ trendMetric: metric });
    }

    /**
     * Block 04's rows, rebuilt only when what they are built from changes. Each query is checked
     * against every other topic's rule, which is worth doing once per retrieval rather than on
     * every render of the page.
     */
    contributorRowsFor(scope) {
        const chunk = this.state.contributors.chunk;
        const cache = this.contributorsCache;
        if (cache && cache.chunk === chunk && cache.scope === scope) return cache.rows;

        const rows = contributorRows(scope, chunk?.rows, scope.topic, scope.contributors);
        this.contributorsCache = { chunk, scope, rows };
        return rows;
    }

    fetchedAt() {
        return this.state.fetchedAt
            ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
            : null;
    }

    exportName(prefix) {
        const scope = this.state.applied;
        return `${prefix}-${scope?.siteUrl ?? 'property'}-${scope?.current?.startDate ?? ''}`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    handleExportComparison() {
        const scope = this.state.applied;
        if (!scope) return;

        downloadCsv(
            buildComparisonCsv({
                t: this.props.t,
                scope,
                rows: orderedComparisonRows(scope, this.state.totals),
                fetchedAt: this.fetchedAt(),
            }),
            `${this.exportName('topic-clusters')}.csv`,
        );
    }

    handleExportContributors() {
        const scope = this.state.applied;
        const chunk = this.state.contributors.chunk;
        if (!scope?.topic || !chunk) return;

        downloadCsv(
            buildContributorsCsv({
                t: this.props.t,
                scope,
                topic: scope.topic,
                view: scope.contributors,
                period: this.contributorsWindow(scope).period,
                // Everything retrieved in this generation, not the page of rows on screen.
                rows: this.contributorRowsFor(scope),
                stopReason: chunk.stopReason,
                fetchedAt: this.fetchedAt(),
            }),
            `${this.exportName(`topic-${scope.topic}-${scope.contributors}`)}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls and the export actions are left out of the frame, while the
     * applied topics and the coverage labels stay in it, so the picture still says what it is a
     * picture of and under which definitions.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-rules') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    // The shared chart and table carry their own refresh and download buttons.
                    element.classList?.contains('chart-action-btns') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName('topic-clusters')}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    /**
     * The coverage strip qualifies the active topic's daily series, the only date-keyed request on
     * the page — so it names whose coverage it is, and says there is none to report while no topic
     * is picked rather than claiming every requested date came back.
     */
    renderCoverage(applied) {
        const { t } = this.props;
        const { trend } = this.state;

        if (!applied.topic) {
            const idle = { status: 'done', coverage: null, error: null };
            return (
                <CoverageStrip
                    scope={applied}
                    current={idle}
                    baseline={applied.baseline ? idle : null}
                    fetchedAt={this.state.fetchedAt}
                    statusNote={t('tools.topic_clusters.coverage.no_topic')}
                />
            );
        }

        const status = trend.status === 'idle' ? 'loading' : trend.status;
        const missing =
            (trend.current?.coverage?.missing?.length ?? 0) +
            (trend.baseline?.coverage?.missing?.length ?? 0);

        return (
            <CoverageStrip
                scope={applied}
                current={{ status, coverage: trend.current?.coverage ?? null, error: trend.error }}
                baseline={
                    applied.baseline
                        ? {
                              status: trend.baselineError ? 'error' : status,
                              coverage: trend.baseline?.coverage ?? null,
                              error: trend.baselineError ?? trend.error,
                          }
                        : null
                }
                fetchedAt={this.state.fetchedAt}
                statusNote={
                    missing
                        ? t('tools.topic_clusters.coverage.some_missing', {
                              topic: applied.topic,
                              missing,
                          })
                        : t('tools.topic_clusters.coverage.all_present', { topic: applied.topic })
                }
            />
        );
    }

    renderReport(applied) {
        const { t } = this.props;
        const { totals, trend, contributors } = this.state;

        const detail = this.contributorsWindow(applied);

        return (
            <>
                {this.renderCoverage(applied)}

                <TopicRules scope={applied} />

                <TopicComparison
                    scope={applied}
                    totals={totals}
                    rows={orderedComparisonRows(applied, totals)}
                    activeTopic={applied.topic}
                    onMode={this.handleMode}
                    onSelect={this.handleSelectTopic}
                    onExport={this.handleExportComparison}
                />

                {/* The two blocks a picked topic fills, wrapped so a pick can bring them into
                    view. */}
                <div ref={this.detailsRef}>
                    <TopicTrend
                        scope={applied}
                        topic={applied.topic}
                        status={trend.status}
                        error={trend.error}
                        baselineError={trend.baselineError}
                        current={trend.current}
                        baseline={trend.baseline}
                        metric={this.trendMetric()}
                        onMetric={this.handleTrendMetric}
                    />

                    <TopicContributors
                        scope={applied}
                        topic={applied.topic}
                        view={applied.contributors}
                        period={detail.period}
                        shortened={detail.shortened}
                        status={contributors.status}
                        error={contributors.error}
                        rows={this.contributorRowsFor(applied)}
                        stopReason={contributors.chunk?.stopReason ?? 'empty_page'}
                        loadingMore={contributors.loadingMore}
                        onView={this.handleContributors}
                        onLoadMore={this.handleLoadMore}
                        onExport={this.handleExportContributors}
                    />
                </div>

                <p className="gsc-tool-table-note">
                    {t('tools.topic_clusters.footnote', { period: formatPeriod(applied.current) })}
                </p>
            </>
        );
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = Object.values(this.state.totals).some((item) => item.status === 'done');

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.topic_clusters.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.topic_clusters.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    extraScopeLabels={
                        applied
                            ? [
                                  t('tools.topic_clusters.controls.topics_applied', {
                                      topics: applied.topics.length,
                                  }),
                                  t(`tools.topic_clusters.modes.${applied.mode}`),
                                  t(
                                      `tools.topic_clusters.contributors.views.${applied.contributors}`,
                                  ),
                              ]
                            : []
                    }
                    footer={
                        <TopicEditor
                            topics={this.state.draft.topics}
                            appliedTopics={applied?.topics ?? null}
                            onChange={this.handleTopicsChange}
                            onRestore={this.handleRestoreTopics}
                        />
                    }
                >
                    <ExtraControls draft={this.state.draft} onChange={this.handleDraftChange} />
                </ScopeControls>

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-keyword_cluster" />
                            </span>
                            <h4>{t('tools.topic_clusters.idle_title')}</h4>
                            <p>{t('tools.topic_clusters.idle')}</p>
                        </div>
                    </div>
                ) : (
                    this.renderReport(applied)
                )}
            </div>
        );
    }
}

export default withTranslation()(TopicClusters);
